import { styled } from '@lichtblick/styled';
import { BorderRadius, Colors } from '@lichtblick/theme';

import { PANEL_LAYOUT_DEFAULTS } from './PanelLayout.constants';

import { ScrollShadowViewport } from '../ScrollShadows';

export const ShellRoot = styled.div`
  --layout-header-height: ${PANEL_LAYOUT_DEFAULTS.headerHeight};
  --layout-header-horizontal-padding: ${PANEL_LAYOUT_DEFAULTS.headerHorizontalPadding};
  --layout-left-sidebar-width: ${PANEL_LAYOUT_DEFAULTS.leftSidebarWidth};
  --layout-main-content-max-width: ${PANEL_LAYOUT_DEFAULTS.mainContentMaxWidth};
  --layout-panel-content-padding: ${PANEL_LAYOUT_DEFAULTS.contentPadding};
  --layout-right-sidebar-width: ${PANEL_LAYOUT_DEFAULTS.rightSidebarWidth};
  --layout-sidebar-collapsed-width: ${PANEL_LAYOUT_DEFAULTS.sidebarCollapsedWidth};
  --layout-sidebar-toggle-border-radius: ${PANEL_LAYOUT_DEFAULTS.sidebarToggleBorderRadius};
  --layout-sidebar-toggle-font-size: ${PANEL_LAYOUT_DEFAULTS.sidebarToggleFontSize};
  --layout-sidebar-toggle-inline-padding: ${PANEL_LAYOUT_DEFAULTS.sidebarToggleInlinePadding};
  --layout-sidebar-toggle-min-size: ${PANEL_LAYOUT_DEFAULTS.sidebarToggleMinSize};
  --layout-sidebar-width: ${PANEL_LAYOUT_DEFAULTS.sidebarWidth};

  display: flex;
  flex-direction: column;
  height: 100vh;
  min-height: 0;
  overflow: hidden;
  width: 100vw;
`;

export const HeadlessShell = styled.div`
  --layout-header-height: ${PANEL_LAYOUT_DEFAULTS.headerHeight};
  --layout-header-horizontal-padding: ${PANEL_LAYOUT_DEFAULTS.headerHorizontalPadding};
  --layout-left-sidebar-width: ${PANEL_LAYOUT_DEFAULTS.leftSidebarWidth};
  --layout-main-content-max-width: ${PANEL_LAYOUT_DEFAULTS.mainContentMaxWidth};
  --layout-panel-content-padding: ${PANEL_LAYOUT_DEFAULTS.contentPadding};
  --layout-right-sidebar-width: ${PANEL_LAYOUT_DEFAULTS.rightSidebarWidth};
  --layout-sidebar-collapsed-width: ${PANEL_LAYOUT_DEFAULTS.sidebarCollapsedWidth};
  --layout-sidebar-toggle-border-radius: ${PANEL_LAYOUT_DEFAULTS.sidebarToggleBorderRadius};
  --layout-sidebar-toggle-font-size: ${PANEL_LAYOUT_DEFAULTS.sidebarToggleFontSize};
  --layout-sidebar-toggle-inline-padding: ${PANEL_LAYOUT_DEFAULTS.sidebarToggleInlinePadding};
  --layout-sidebar-toggle-min-size: ${PANEL_LAYOUT_DEFAULTS.sidebarToggleMinSize};
  --layout-sidebar-width: ${PANEL_LAYOUT_DEFAULTS.sidebarWidth};

  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  width: 100%;
`;

export const FixedHeader = styled.header<{ $hidden?: boolean }>`
  background: var(--colors-white);
  border-bottom: ${({ $hidden }) => ($hidden ? 'none' : '1px solid var(--colors-blue-20)')};
  position: sticky;
  top: 0;
`;

export const FixedHeaderInner = styled.div`
  align-items: center;
  display: flex;
  gap: var(--spacing-s);
  height: 100%;
  padding: 0 var(--layout-header-horizontal-padding);
`;

export const HeaderPrimary = styled.div`
  align-items: flex-start;
  display: flex;
  flex: 1 1 auto;
  flex-wrap: wrap;
  gap: var(--spacing-s);
  min-width: 0;
  padding-bottom: var(--spacing-xs);
`;

export const DefaultHeaderWrapper = styled.div`
  align-items: center;
  display: flex;
  flex: 0 1 auto;
  flex-direction: row;
  gap: var(--spacing-xxs);
  margin-bottom: var(--spacing-xxs);
  margin-top: var(--spacing-xs);
  min-width: 0;
`;

export const DefaultHeaderTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  margin: 0;
`;

export const PanelContentInner = styled.div<{ $padded?: boolean }>`
  box-sizing: border-box;
  min-height: 100%;
  padding: ${({ $padded }) => ($padded ? 'var(--layout-panel-content-padding)' : '0')};
  width: 100%;
`;

export const ScrollViewport = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  position: relative;
`;

export const SidebarControlButtonRoot = styled.button<{ $active: boolean; $visible: boolean }>`
  align-items: center;
  background-color: ${({ $active }) => ($active ? Colors.blue['100'] : '#F8FBFD')};
  border: none;
  border-radius: ${BorderRadius.Xs};
  color: ${({ $active }) => ($active ? Colors.white : Colors.blue['100'])};
  cursor: pointer;
  display: inline-flex;
  height: 100%;
  justify-content: center;
  min-height: 2rem;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  padding: var(--spacing-xxs) var(--spacing-xxs);
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition:
    color 0.25s,
    background-color 0.1s;
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};

  &:disabled {
    cursor: not-allowed;
  }
`;

export const SidebarControlButtonIconContainer = styled.div`
  align-items: center;
  display: inline-flex;
  height: 1.5rem;
  justify-content: center;
  width: 1.5rem;
`;

export const MainPanel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  position: relative;
`;

export const FloatingSidebarControls = styled.div`
  display: inline-flex;
  gap: var(--spacing-xs);
  position: absolute;
  right: var(--spacing-s);
  top: var(--spacing-s);
  z-index: var(--z-index-panel-overlay);
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.14));
`;

export const MainScrollViewport = styled(ScrollShadowViewport)`
  flex: 1 1 0%;
`;

export const MainScrollArea = styled.main`
  box-sizing: border-box;
  flex: 1 1 0%;
  margin: 0;
  min-height: 0;
  min-width: 0;
  overflow: auto;
  overscroll-behavior-x: contain;
  position: relative;
  width: 100%;
`;

export const RightPanelSidebar = styled.aside<{ $collapsed?: boolean }>`
  background: var(--colors-white);
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  overscroll-behavior-x: contain;
  position: relative;
  border-left: 1px solid ${({ $collapsed }) => ($collapsed ? 'transparent' : 'var(--colors-blue-20)')};
  opacity: ${({ $collapsed }) => ($collapsed ? 0 : 1)};
  transition:
    border-left-color 0.2s ease,
    opacity 0.15s ease;
`;

export const RightPanelSidebarInner = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`;

export const RightPanelSidebarScrollViewport = styled(ScrollShadowViewport)`
  flex: 1;
`;

export const RightPanelSidebarScrollArea = styled.div<{ $padded?: boolean }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: auto;
  overscroll-behavior: contain;
  padding: ${({ $padded }) => ($padded ? 'var(--layout-panel-content-padding)' : '0')};
  width: 100%;
`;

const getThreePanelColumns = ({
  hasLeftSidebar,
  hasRightSidebar,
  leftCollapsed,
  rightCollapsed,
}: {
  hasLeftSidebar: boolean;
  hasRightSidebar: boolean;
  leftCollapsed: boolean;
  rightCollapsed: boolean;
}): string => {
  // Use 0px rather than omitting the column so grid-template-columns can transition smoothly.
  const left = hasLeftSidebar ? (leftCollapsed ? '0px' : 'var(--layout-left-sidebar-width)') : null;
  const right = hasRightSidebar ? (rightCollapsed ? '0px' : 'var(--layout-right-sidebar-width)') : null;

  return [left, 'minmax(0, 1fr)', right].filter(Boolean).join(' ');
};

export const ThreePanelBody = styled.div<{
  $hasLeftSidebar?: boolean;
  $hasRightSidebar?: boolean;
  $isDrawerMode?: boolean;
  $leftCollapsed?: boolean;
  $rightCollapsed?: boolean;
}>`
  display: grid;
  flex: 1 1 auto;
  grid-template-columns: ${({ $hasLeftSidebar, $hasRightSidebar, $isDrawerMode, $leftCollapsed, $rightCollapsed }) => {
    if ($isDrawerMode) {
      return 'minmax(0, 1fr)';
    }

    return getThreePanelColumns({
      hasLeftSidebar: $hasLeftSidebar ?? false,
      hasRightSidebar: $hasRightSidebar ?? false,
      leftCollapsed: $leftCollapsed ?? false,
      rightCollapsed: $rightCollapsed ?? false,
    });
  }};
  min-height: 0;
  overflow: ${({ $isDrawerMode }) => ($isDrawerMode ? 'visible' : 'hidden')};
  position: relative;
  transition: grid-template-columns 0.2s ease;
`;

export const LeftPanelSidebar = styled.aside<{ $collapsed?: boolean }>`
  background: var(--colors-white);
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  overscroll-behavior-x: contain;
  position: relative;
  border-right: 1px solid ${({ $collapsed }) => ($collapsed ? 'transparent' : 'var(--colors-blue-20)')};
  opacity: ${({ $collapsed }) => ($collapsed ? 0 : 1)};
  transition:
    border-right-color 0.2s ease,
    opacity 0.15s ease;
`;

export const LeftPanelSidebarInner = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`;

export const LeftPanelSidebarScrollViewport = styled(ScrollShadowViewport)`
  flex: 1;
`;

export const LeftPanelSidebarScrollArea = styled.div<{ $padded?: boolean }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: auto;
  overscroll-behavior: contain;
  padding: ${({ $padded }) => ($padded ? 'var(--layout-panel-content-padding)' : '0')};
  width: 100%;
`;

export const PanelLoaderOverlay = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  inset: 0;
  pointer-events: none;
  position: absolute;
  z-index: var(--z-index-panel-loader);
`;

// ─── Inner layout primitives (no shell / header — composable inside pages) ───

export const InnerPanelRoot = styled.div<{ $isDrawerMode?: boolean }>`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow: ${({ $isDrawerMode }) => ($isDrawerMode ? 'visible' : 'hidden')};
  position: relative;
`;

export const SidebarControlsContainer = styled.div<{ $mobileRightAligned?: boolean }>`
  align-items: center;
  border-radius: var(--border-radius-s);
  display: inline-flex;
  flex: 0 0 auto;
  gap: 0;
  margin-left: ${({ $mobileRightAligned }) => ($mobileRightAligned ? 'auto' : '0')};
`;
